import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const VerifyOtp = () => {
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [countdown, setCountdown] = useState(300); // 5 minutes in seconds
    const [isResending, setIsResending] = useState(false);

    const inputRefs = useRef([]);
    const navigate = useNavigate();
    const location = useLocation();

    // Registration data passed from Register.js
    const { email, fullName, phone, password } = location.state || {};

    // Redirect back if no state
    useEffect(() => {
        if (!email || !password) {
            navigate('/register');
        }
    }, [email, password, navigate]);

    // Countdown timer
    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setInterval(() => {
            setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(timer);
    }, [countdown]);

    const formatTime = (secs) => {
        const m = Math.floor(secs / 60).toString().padStart(2, '0');
        const s = (secs % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    // Handle OTP digit input with auto-advance
    const handleOtpChange = (index, value) => {
        if (!/^\d*$/.test(value)) return; // digits only
        const newOtp = [...otp];
        newOtp[index] = value.slice(-1); // take last char
        setOtp(newOtp);
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (!text) return;
        const newOtp = Array(6).fill('');
        text.split('').forEach((char, i) => { newOtp[i] = char; });
        setOtp(newOtp);
        // Focus last filled or next empty
        const lastIdx = Math.min(text.length, 5);
        inputRefs.current[lastIdx]?.focus();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        const otpCode = otp.join('');
        if (otpCode.length < 6) {
            setError('Vui lòng nhập đủ 6 chữ số của mã OTP.');
            return;
        }

        if (countdown <= 0) {
            setError('Mã OTP đã hết hạn. Vui lòng yêu cầu mã mới.');
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/client/auth/otp/verify`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'accept': 'application/json',
                },
                body: JSON.stringify({ email, otp: otpCode }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Xác thực thất bại. Vui lòng thử lại.');
            }

            setSuccess('Đăng ký thành công! Đang chuyển đến trang đăng nhập...');
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResend = async () => {
        if (countdown > 0) return;
        setError('');
        setIsResending(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/client/auth/otp/send`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'accept': 'application/json',
                },
                body: JSON.stringify({ email, full_name: fullName, phone, password }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Không thể gửi lại mã. Vui lòng thử lại.');
            }

            setOtp(['', '', '', '', '', '']);
            setCountdown(300);
            inputRefs.current[0]?.focus();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsResending(false);
        }
    };

    return (
        <div className="relative flex min-h-screen w-full flex-row overflow-hidden">
            {/* Left Side: Visual/Hero Area */}
            <div className="hidden lg:flex lg:w-1/2 relative flex-col justify-between bg-blue-50 dark:bg-slate-900 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div
                        className="w-full h-full bg-cover bg-center"
                        style={{ backgroundImage: "url('https://lh3.googleusercontent.com/aida-public/AB6AXuBRA9YtQ2UW1z-cJjzuMred8ga7vPzoZQFETZRL1FfhwiudEn9EUAiXPTMfcGfmcQZ0Z6DZvPibfu-cH0qvBMo0EGKAMO_NNiaDLVwLIWj9V8Z6TyMauWo-rKIwHrw9cTbG70YiXSsanDPSdqWXyDVOq7QN_lYp-GNezeioKQDUQ2P_VQEROGzCJrQZfoUsa0iiax1xJ4j8nngCdZjsQH3312VmJfB8rOKdky41uKR8nts9G9vl0Wu6SGB32c5ax-MnahOW8b-k37o')" }}
                    >
                        <div className="absolute inset-0 bg-primary-blue/20 mix-blend-multiply"></div>
                    </div>
                </div>
                <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white drop-shadow-md">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <span className="material-symbols-outlined text-white">school</span>
                        </div>
                        <span className="text-2xl font-black tracking-tight">Flyers Prep</span>
                    </div>
                    <div className="mb-10">
                        <h2 className="text-4xl font-bold leading-tight mb-4 text-white drop-shadow-lg">
                            Gần xong rồi! <br /> Xác thực email của bạn
                        </h2>
                        <p className="text-lg font-medium text-white/90 max-w-md drop-shadow-md">
                            Nhập mã 6 chữ số được gửi đến email của bạn để hoàn tất đăng ký.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right Side: OTP Form */}
            <div className="w-full lg:w-1/2 flex flex-col items-center justify-center bg-white dark:bg-background-dark p-6 sm:p-12 md:p-16 relative overflow-y-auto">
                {/* Mobile Logo */}
                <div className="lg:hidden absolute top-8 left-8 flex items-center gap-2 text-primary-blue dark:text-white">
                    <span className="material-symbols-outlined text-3xl">school</span>
                    <span className="text-xl font-bold">Flyers Prep</span>
                </div>

                <div className="w-full max-w-[420px] flex flex-col gap-6 py-16 lg:py-0">
                    {/* Header */}
                    <div className="flex flex-col gap-2 text-center lg:text-left">
                        {/* Shield icon */}
                        <div className="flex justify-center lg:justify-start mb-2">
                            <div className="h-14 w-14 bg-blue-50 rounded-2xl flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary-blue text-3xl">verified_user</span>
                            </div>
                        </div>
                        <h1 className="text-[#0d161c] dark:text-white text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">
                            Xác thực OTP
                        </h1>
                        <p className="text-slate-500 dark:text-slate-400 text-base font-normal">
                            Chúng tôi đã gửi mã 6 chữ số đến{' '}
                            <span className="font-semibold text-primary-blue">{email}</span>
                        </p>
                    </div>

                    <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
                        {/* OTP Inputs */}
                        <div className="flex flex-col gap-2">
                            <label className="text-[#0d161c] dark:text-white text-sm font-bold pl-1">
                                Mã xác thực OTP
                            </label>
                            <div className="flex gap-3 justify-between" onPaste={handlePaste}>
                                {otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        id={`otp-${index}`}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleOtpChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        className={`w-12 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all outline-none
                      text-[#0d161c] dark:text-white bg-background-light dark:bg-slate-800
                      ${digit
                                                ? 'border-primary-blue bg-blue-50 dark:bg-blue-900/20'
                                                : 'border-[#cedee8] dark:border-slate-700'
                                            }
                      focus:border-primary-blue focus:ring-2 focus:ring-primary-blue/20`}
                                    />
                                ))}
                            </div>

                            {/* Countdown */}
                            <div className="flex items-center justify-between mt-1">
                                <p className="text-slate-400 text-xs">
                                    {countdown > 0 ? (
                                        <>
                                            Mã hết hạn sau{' '}
                                            <span className={`font-bold ${countdown <= 60 ? 'text-red-500' : 'text-primary-blue'}`}>
                                                {formatTime(countdown)}
                                            </span>
                                        </>
                                    ) : (
                                        <span className="text-red-500 font-medium">Mã OTP đã hết hạn</span>
                                    )}
                                </p>
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={countdown > 0 || isResending}
                                    className="text-xs font-semibold text-primary-blue hover:underline disabled:text-slate-400 disabled:cursor-not-allowed disabled:no-underline transition-colors"
                                >
                                    {isResending ? 'Đang gửi...' : 'Gửi lại mã'}
                                </button>
                            </div>
                        </div>

                        {/* Error message */}
                        {error && (
                            <div className="flex items-start gap-2 text-red-600 text-sm text-center bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                                <span className="material-symbols-outlined text-[18px] flex-shrink-0 mt-0.5">error</span>
                                <span>{error}</span>
                            </div>
                        )}

                        {/* Success message */}
                        {success && (
                            <div className="flex items-start gap-2 text-green-600 text-sm text-center bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                                <span className="material-symbols-outlined text-[18px] flex-shrink-0 mt-0.5">check_circle</span>
                                <span>{success}</span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isLoading || otp.join('').length < 6}
                            className="flex w-full cursor-pointer items-center justify-center rounded-xl h-14 px-5 bg-primary-blue hover:bg-blue-400 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-lg font-bold leading-normal tracking-[0.015em] shadow-lg shadow-blue-200 dark:shadow-none transition-all active:scale-[0.98]"
                        >
                            {isLoading ? (
                                <>
                                    <span className="material-symbols-outlined text-[20px] mr-2 animate-spin">progress_activity</span>
                                    <span className="truncate">Đang xác thực...</span>
                                </>
                            ) : (
                                <>
                                    <span className="truncate">Xác thực & Tạo tài khoản</span>
                                    <span className="material-symbols-outlined ml-2 text-[20px]">verified</span>
                                </>
                            )}
                        </button>
                    </form>

                    {/* Back to Register */}
                    <div className="flex flex-col items-center gap-2 mt-1">
                        <p className="text-[#0d161c] dark:text-slate-300 text-sm font-normal text-center">
                            Nhập sai thông tin?{' '}
                            <Link
                                to="/register"
                                className="text-primary-blue hover:text-blue-600 font-bold transition-colors inline-flex items-center gap-1 group"
                            >
                                Quay lại đăng ký
                                <span className="material-symbols-outlined text-[16px] group-hover:-translate-x-1 transition-transform">
                                    arrow_back
                                </span>
                            </Link>
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="absolute bottom-6 text-center w-full px-6">
                    <p className="text-slate-400 dark:text-slate-600 text-xs">
                        © 2024 Flyers Prep. Helping kids soar.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default VerifyOtp;
